import type { Ref } from 'vue'
import { computed, ref } from 'vue'
import { usePointerSwipe, useSwipe, type UseSwipeDirection } from '@vueuse/core'
import { AxisDirection } from '@collection-player/composables/slide-direction.use'

const MIN_DISTANCE_TO_SWIPE = 150

export default (
  element: Ref<HTMLElement | null>,
  actions: {
    onSwipeLeft: () => void
    onSwipeRight: () => void
    onSwipeUp: () => void
    onSwipeDown: () => void
  },
  slideAxis: Ref<AxisDirection | undefined>,
) => {
  const groupAxisDirection = computed(() =>
    slideAxis.value === AxisDirection.HORIZONTAL ? AxisDirection.VERTICAL : AxisDirection.HORIZONTAL,
  )

  const containerDimension = computed(() =>
    groupAxisDirection.value === AxisDirection.HORIZONTAL ? element.value?.offsetWidth : element.value?.offsetHeight,
  )

  const offset = ref('0')
  const isUsed = ref(false)
  const gestureStyling = computed(() => ({
    transform:
      offset.value === '0'
        ? undefined
        : groupAxisDirection.value === AxisDirection.HORIZONTAL
          ? `translateX(${offset.value})`
          : `translateY(${offset.value})`,
    transition: isUsed.value ? undefined : 'transform 0.3s',
  }))

  const resetOffset = () => (offset.value = '0')

  const isGroupChangeDirection = (direction: UseSwipeDirection) =>
    groupAxisDirection.value === AxisDirection.HORIZONTAL
      ? direction === 'up' || direction === 'down' || direction === 'none'
      : direction === 'left' || direction === 'right' || direction === 'none'

  const handleSwipe = (event: TouchEvent | PointerEvent, direction: UseSwipeDirection, distance: number) => {
    if (!containerDimension.value) {
      return
    }

    if (isGroupChangeDirection(direction)) {
      resetOffset()
      return
    }

    isUsed.value = true
    offset.value = `${-distance / 3}px`
  }

  const handleSwipeEnd = (
    event: TouchEvent | PointerEvent,
    direction: UseSwipeDirection,
    distance: number,
    isPassive?: boolean,
  ) => {
    resetOffset()
    isUsed.value = false

    if (isGroupChangeDirection(direction) || Math.abs(distance) < MIN_DISTANCE_TO_SWIPE) {
      return
    }

    if (!isPassive) {
      event.preventDefault()
    }

    event.stopPropagation()

    if (groupAxisDirection.value === AxisDirection.HORIZONTAL) {
      if (distance < 0) {
        actions.onSwipeLeft()
      } else if (distance > 0) {
        actions.onSwipeRight()
      }
    } else {
      if (distance < 0) {
        actions.onSwipeUp()
      } else if (distance > 0) {
        actions.onSwipeDown()
      }
    }
  }

  const {
    direction: pointerDirection,
    distanceX: pointerDistanceX,
    distanceY: pointerDistanceY,
  } = usePointerSwipe(element, {
    onSwipe: (event) =>
      handleSwipe(
        event,
        pointerDirection.value,
        groupAxisDirection.value === AxisDirection.HORIZONTAL ? pointerDistanceX.value : pointerDistanceY.value,
      ),
    onSwipeEnd: (event) =>
      handleSwipeEnd(
        event,
        pointerDirection.value,
        groupAxisDirection.value === AxisDirection.HORIZONTAL ? pointerDistanceX.value : pointerDistanceY.value,
      ),
  })

  const {
    direction: touchDirection,
    lengthX: touchLengthX,
    lengthY: touchLengthY,
  } = useSwipe(element, {
    passive: true,
    onSwipe: (event) =>
      handleSwipe(
        event,
        touchDirection.value,
        groupAxisDirection.value === AxisDirection.HORIZONTAL ? touchLengthX.value : touchLengthY.value,
      ),
    onSwipeEnd: (event) =>
      handleSwipeEnd(
        event,
        touchDirection.value,
        groupAxisDirection.value === AxisDirection.HORIZONTAL ? touchLengthX.value : touchLengthY.value,
        true,
      ),
  })

  return {
    touchDirection,
    gestureStyling,
  }
}
